import PropTypes from "prop-types";
import React, { useEffect, useMemo, useRef, useState } from "react";

import { getFocusElement, window } from "@swa-ui/browser";
import { swaDate } from "@swa-ui/date";

import { keyCodes } from "../defines";
import { Input } from "../Input";

const DATE_VALUE_FORMAT_LENGTH = 2;
const DAY_FALLBACK = "01";
const FORMAT_DELIMITER = "/";
const LONG_DATE_FORMAT = "MM/DD/YYYY";
const MONTH_FALLBACK = "01";
const YEAR_LONG_FALLBACK = "0000";
const YEAR_LONG_FORMAT_LENGTH = 4;
const YEAR_SHORT_FALLBACK = "2000";

/**
 * DateInput renders an input field that mimics the a11y compliant navigation of a standard
 * HTML input field with type of "date", standardizing the experience across browsers and
 * allowing for custom formatting. Provided formats MUST separate the month, day, and year
 * with "/".
 */
export const DateInput = React.forwardRef((props, ref) => {
  const {
    "aria-describedby": ariaDescribedBy,
    "aria-label": ariaLabel,
    "aria-required": ariaRequired,
    className,
    disabled,
    error,
    format = "MM/DD",
    id,
    inputMode,
    name,
    onBlur,
    onClick,
    onChange,
    onFocus,
    onKeyDown,
    onMouseDown,
    required,
    value,
  } = props;
  const [day, setDay] = useState("");
  const [month, setMonth] = useState("");
  const [year, setYear] = useState("");
  const [selectedSection, setSelectedSection] = useState(null);
  const sections = useMemo(() => getSectionsFromFormat(), [format]);
  const sectionName = useMemo(() => sections[selectedSection]?.name, [selectedSection, sections]);
  const currentDate = useMemo(() => createDate(month, day, year), [day, format, month, year]);
  const inputRef = useRef(null);
  const clickRef = useRef(false);
  const focusDirectionRef = useRef(null);
  const doubleDigitMonthRef = useRef(false);
  const doubleDigitDayRef = useRef(false);
  const yearDigitsEnteredRef = useRef(0);

  useEffect(() => {
    if (hasFocus()) {
      const { selectionStart, selectionEnd } = inputRef.current;

      if (selectionStart === selectionEnd && selectedSection !== null) {
        const { start, end } = sections[selectedSection];

        inputRef.current.setSelectionRange(start, end);
      }
    }
  });

  useEffect(() => {
    window.addEventListener("keydown", updateFocusDirection); // NOSONAR

    return () => {
      window.removeEventListener("keydown", updateFocusDirection); // NOSONAR
    };
  }, []);

  useEffect(() => {
    const dateFromValue = swaDate(value);
    let newValue = dateFromValue;

    if (!dateFromValue.isValid()) {
      newValue = swaDate(undefined, LONG_DATE_FORMAT);
    }

    setDay(newValue.format("DD"));
    setMonth(newValue.format("MM"));
    setYear(newValue.format("YYYY"));
  }, [value]);

  useEffect(() => {
    if (selectedSection !== null && inputRef.current) {
      const { start, end } = sections[selectedSection];

      inputRef.current.setSelectionRange(start, end);
      inputRef.current.focus();
    }
  }, [selectedSection, day, month, year]);

  useEffect(() => {
    if (sectionName !== "month") {
      doubleDigitMonthRef.current = false;
    } else if (sectionName !== "day") {
      doubleDigitDayRef.current = false;
    }
  }, [selectedSection]);

  useEffect(() => {
    if (hasFocus() && onChange) {
      const event = { target: { value: formatDateValue() } };

      onChange(event);
    }
  }, [currentDate]);

  return <Input {...getInputProps()} />;

  function getInputProps() {
    return {
      "aria-describedby": ariaDescribedBy,
      "aria-label": ariaLabel,
      "aria-required": ariaRequired,
      className,
      disabled,
      error,
      id,
      inputMode,
      name,
      onBlur: handleInputBlur,
      onChange: handleInputChange,
      onClick: handleInputClick,
      onFocus: handleInputFocus,
      onKeyDown: handleInputKeyDown,
      onMouseDown: handleInputMouseDown,
      ref: handleMultipleRefs,
      required,
      role: "spinbutton",
      suffixIcon: { name: "Calendar" },
      value: formatDateValue(),
    };
  }

  function handleInputChange(event) {
    onChange?.(event);
  }

  function handleInputBlur() {
    setSelectedSection(null);
    resetSectionDigitCounts();
    onBlur?.();
  }

  function handleInputClick(event) {
    const { selectionStart } = event.target;

    sections.forEach((sectionDetails, index) => {
      if (selectionStart >= sectionDetails.start && selectionStart <= sectionDetails.end) {
        setSelectedSection(index);
        clickRef.current = false;
        event.preventDefault();
        onClick?.();
      }
    });
  }

  function handleInputMouseDown(event) {
    clickRef.current = true;
    focusDirectionRef.current = null;
    onMouseDown?.(event);
  }

  function handleInputFocus() {
    const lastSection = sections.length - 1;
    const sectionToFocus = focusDirectionRef.current === "backward" ? lastSection : 0;

    !clickRef.current && setSelectedSection(sectionToFocus);
    clickRef.current = false;
    onFocus?.();
  }

  function handleInputKeyDown(event) {
    const { key } = event;

    if (/\d/.test(key) && selectedSection !== null) {
      event.preventDefault();
      event.stopPropagation();

      enterDigitInDateSubsection(key);
    } else {
      if (key === keyCodes.KEY_TAB) {
        const isLastSection = selectedSection === sections.length - 1;
        const isFirstSection = selectedSection === 0;

        if (event.shiftKey) {
          if (!isFirstSection) {
            event.preventDefault();
            selectPreviousSection();
          }
        } else {
          if (!isLastSection) {
            event.preventDefault();
            selectNextSection();
          }
        }
      } else {
        event.preventDefault();

        if (key === keyCodes.KEY_LEFT) {
          selectPreviousSection();
        } else if (key === keyCodes.KEY_RIGHT) {
          selectNextSection();
        } else if (key === keyCodes.KEY_BACKSPACE) {
          resetSectionDigitCounts();

          if (sectionName === "month") {
            setMonth(MONTH_FALLBACK);
          } else if (sectionName === "day") {
            setDay(DAY_FALLBACK);
          } else if (sectionName === "year") {
            if (isLongYearFormat()) {
              setYear(YEAR_LONG_FALLBACK);
            } else {
              setYear(YEAR_SHORT_FALLBACK);
            }
          }
        } else if (key === keyCodes.KEY_UP) {
          if (sectionName === "month") {
            const newDate = currentDate.add(1, "month");
            const isMonthIncrementedByOne = newDate.get("date") !== currentDate.get("date");

            if (newDate.isSame(currentDate, "year")) {
              setMonth(newDate.format("MM"));

              if (isMonthIncrementedByOne) {
                setDay(newDate.format("DD"));
              }
            } else {
              setMonth("01");
            }
          } else if (sectionName === "day") {
            const nextDay = currentDate.add(1, "day");

            if (nextDay.isSame(currentDate, "month")) {
              setDay(nextDay.format("DD"));
            } else {
              setDay("01");
            }
          } else if (sectionName === "year") {
            const nextYear = parseInt(year) + 1;

            setYear(`${nextYear}`);
          }
        } else if (key === keyCodes.KEY_DOWN) {
          if (sectionName === "month") {
            const newDate = currentDate.subtract(1, "month");
            const isMonthDecrementedByOne = newDate.get("date") !== currentDate.get("date");

            if (newDate.isSame(currentDate, "year")) {
              setMonth(newDate.format("MM"));

              if (isMonthDecrementedByOne) {
                setDay(newDate.format("DD"));
              }
            } else {
              setMonth("12");
            }
          } else if (sectionName === "day") {
            const previousDay = currentDate.subtract(1, "day");

            if (previousDay.isSame(currentDate, "month")) {
              setDay(previousDay.format("DD"));
            } else {
              setDay(currentDate.endOf("month").format("DD"));
            }
          } else if (sectionName === "year") {
            const nextYear = parseInt(year) - 1;

            setYear(`${nextYear}`);
          }
        }
      }
    }

    onKeyDown?.(event);
  }

  function handleMultipleRefs(element) {
    inputRef.current = element;

    if (typeof ref === "object" && ref !== null) {
      ref.current = element;
    } else if (typeof ref === "function") {
      ref(element);
    }
  }

  function createDate(newMonth, newDay, newYear) {
    const paddedMonth = `${parseInt(newMonth)}`.padStart(DATE_VALUE_FORMAT_LENGTH, "0");
    const paddedDay = `${parseInt(newDay)}`.padStart(DATE_VALUE_FORMAT_LENGTH, "0");
    const paddedYear = `${parseInt(newYear)}`.padStart(4, "0");

    return swaDate(`${paddedMonth}/${paddedDay}/${paddedYear}`, LONG_DATE_FORMAT);
  }

  function enterDigitInDateSubsection(digit) {
    if (sectionName === "month") {
      enterDigitInMonth(digit);
    } else if (sectionName === "day") {
      enterDigitInDay(digit);
    } else {
      enterDigitInYear(digit);
    }
  }

  function enterDigitInDay(digit) {
    const combinedDate = `${day.slice(1)}${digit}`;
    const isValidDoubleDigitDay = createDate(month, combinedDate, year).isSame(
      currentDate,
      "month"
    );
    const isValidFirstDayDigit = digit === "0" || digit === "1" || digit === "2" || digit === "3";

    if (doubleDigitDayRef.current && isValidDoubleDigitDay) {
      setDay(`${combinedDate}`);
      selectNextSection();
      doubleDigitDayRef.current = false;
    } else if (isValidFirstDayDigit) {
      setDay(digit.padStart(DATE_VALUE_FORMAT_LENGTH, "0"));
      doubleDigitDayRef.current = true;
    } else {
      setDay(digit.padStart(DATE_VALUE_FORMAT_LENGTH, "0"));
      selectNextSection();
      doubleDigitDayRef.current = false;
    }
  }

  function enterDigitInMonth(digit) {
    const isValidSecondDigit = digit === "0" || digit === "1" || digit === "2";

    if (doubleDigitMonthRef.current && isValidSecondDigit) {
      setMonth(`1${digit}`);
      selectNextSection();
      doubleDigitMonthRef.current = false;
    } else if (digit === "1") {
      setMonth(digit.padStart(DATE_VALUE_FORMAT_LENGTH, "0"));
      doubleDigitMonthRef.current = true;
    } else if (digit === "0") {
      setMonth(digit.padStart(DATE_VALUE_FORMAT_LENGTH, "0"));
      doubleDigitMonthRef.current = false;
    } else {
      setMonth(digit.padStart(DATE_VALUE_FORMAT_LENGTH, "0"));
      selectNextSection();
      doubleDigitMonthRef.current = false;
    }
  }

  function enterDigitInYear(digit) {
    const combinedYear = `${year.slice(1)}${digit}`;
    const yearDigitsEntered = yearDigitsEnteredRef.current + 1;

    if (isLongYearFormat()) {
      if (yearDigitsEntered < YEAR_LONG_FORMAT_LENGTH) {
        if (yearDigitsEntered === 1) {
          setYear(digit.padStart(YEAR_LONG_FORMAT_LENGTH, "0"));
        } else {
          const paddedYear = combinedYear.padEnd(YEAR_LONG_FORMAT_LENGTH, "0");

          setYear(paddedYear);
        }

        yearDigitsEnteredRef.current = yearDigitsEntered;
      } else {
        setYear(combinedYear);
        selectNextSection();
        yearDigitsEnteredRef.current = 0;
      }
    } else if (!isLongYearFormat()) {
      if (yearDigitsEntered < 2) {
        setYear(`20${digit.padStart(DATE_VALUE_FORMAT_LENGTH, "0")}`);
        yearDigitsEnteredRef.current = yearDigitsEntered;
      } else {
        setYear(`20${year[3]}${digit}`);
        selectNextSection();
        yearDigitsEnteredRef.current = 0;
      }
    }
  }

  function formatDateValue() {
    return currentDate.year(year).format(format);
  }

  function getSectionsFromFormat() {
    const sectionsFromFormat = format.split(FORMAT_DELIMITER);

    return sectionsFromFormat.reduce((sectionDetails, sectionWithFormatString) => {
      const normalizedFormatString = sectionWithFormatString.toUpperCase();
      const details = {};

      if (normalizedFormatString.includes("M")) {
        details.name = "month";
      } else if (normalizedFormatString.includes("D")) {
        details.name = "day";
      } else {
        details.name = "year";
      }

      details.start =
        sectionDetails.length > 0 ? sectionDetails[sectionDetails.length - 1].end + 1 : 0;
      details.end = details.start + normalizedFormatString.length;
      details.formatString = normalizedFormatString;

      return [...sectionDetails, details];
    }, []);
  }

  function hasFocus() {
    return inputRef.current === getFocusElement();
  }

  function isLongYearFormat() {
    return sections.some((section) => section.name === "year" && section.formatString.length === 4);
  }

  function resetSectionDigitCounts() {
    doubleDigitMonthRef.current = false;
    doubleDigitDayRef.current = false;
    yearDigitsEnteredRef.current = 0;
  }

  function selectNextSection() {
    const nextSection = Math.min(selectedSection + 1, sections.length - 1);

    if (selectedSection !== null && selectedSection !== nextSection) {
      setSelectedSection(nextSection);
    }
  }

  function selectPreviousSection() {
    const previousSection = Math.max(selectedSection - 1, 0);

    if (selectedSection !== null && selectedSection !== previousSection) {
      setSelectedSection(previousSection);
    }
  }

  function updateFocusDirection(event) {
    const { key } = event;

    if (key === keyCodes.KEY_TAB) {
      focusDirectionRef.current = event.shiftKey ? "backward" : "forward";
    }
  }
});

DateInput.displayName = "DateInput";

DateInput.propTypes = {
  /** ID of the element that describes the input for accessibility. */
  "aria-describedby": PropTypes.string,

  /** aria-label text to provide additional accessibility description of input element. */
  "aria-label": PropTypes.string,

  /** Indicates whether the input is required for accessibility. */
  "aria-required": PropTypes.bool,

  /** Additional classes for positioning the component. */
  className: PropTypes.string,

  /** Indicates Input should be disabled. */
  disabled: PropTypes.bool,

  /** Error message or boolean to indicate error state. */
  error: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),

  /** Format of the date value. */
  format: PropTypes.string,

  /** Unique identifier for the input element. */
  id: PropTypes.string,

  /** Specifies what kind of input mechanism would be most helpful for users entering content. */
  inputMode: PropTypes.string,

  /** Name attribute for the input element. */
  name: PropTypes.string,

  /** Function called when the input loses focus. */
  onBlur: PropTypes.func,

  /** Function called when the input value changes. */
  onChange: PropTypes.func,

  /** Function called when the input is clicked. */
  onClick: PropTypes.func,

  /** Function called when the input receives focus. */
  onFocus: PropTypes.func,

  /** Function called when a key is pressed down while the input has focus. */
  onKeyDown: PropTypes.func,

  /** Function called when a mouse button is pressed down on the input. */
  onMouseDown: PropTypes.func,

  /** Indicates whether the input is required. */
  required: PropTypes.bool,

  /** Date value in MM/DD/YYYY format. */
  value: PropTypes.string,
};
